import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";

import SaveCancel from "../../../components/SaveCancel";
import TabPanel from "../../../components/TabPanel";
import { a11yProps } from "../../../utils/common";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import useMapUser from "../../../hooks/useMapUser";
import DotSettingsFields from "./components/DotSettingsFields";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);

const PageWrapper = styled(Box)`
  padding: ${(props) => props.theme.spacing(4)};
  background: ${(props) => props.theme.palette.background.default};
  min-height: 100vh;
`;

const StyledCard = styled(Card)`
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid ${(props) => props.theme.palette.divider};
`;

const HeaderContainer = styled(Box)`
  padding: ${(props) => props.theme.spacing(6)} ${(props) => props.theme.spacing(6)} 0;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.palette.primary.main} 0%,
    ${(props) => props.theme.palette.primary.dark} 100%
  );
  color: white;
  margin-bottom: -1px;
`;

const FormSection = styled(Box)`
  padding: ${(props) => props.theme.spacing(4)};
`;

const TabContainer = styled(Box)`
  background: white;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  padding: 0 ${(props) => props.theme.spacing(4)};
`;

const StyledTabs = styled(Tabs)`
  & .MuiTabs-indicator {
    height: 3px;
    border-radius: 3px;
  }
`;

const StyledTab = styled(Tab)`
  text-transform: none;
  font-weight: 600;
  font-size: 0.95rem;
  padding: ${(props) => props.theme.spacing(4)};
  min-width: 160px;
`;

const validationSchema = Yup.object().shape({
  userId: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("User is required"),
});

function EditMapUser() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();

  const { getMappingByUserId, updateMapUser, isSubmitting, isLoading, mapUser } = useMapUser();

  const [tabValue, setTabValue] = React.useState(0);

  const methods = useForm({
    defaultValues: {
      userId: null,
      franchiseIds: [],
      radUserIds: [],
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (state && state.id) {
      getMappingByUserId(state.id);
    }
  }, [state]);

  useEffect(() => {
    if (mapUser && Object.keys(mapUser).length > 0) {
      reset({
        userId: {
          label: mapUser.adminName || mapUser.adminUsername || mapUser.userId,
          value: mapUser.userId,
        },
        franchiseIds: mapUser.franchiseIds || [],
        radUserIds: mapUser.radUserIds || [],
      });
    }
  }, [mapUser, reset]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        userId: data.userId.value,
        franchiseIds: data.franchiseIds,
        radUserIds: data.radUserIds,
      };

      await updateMapUser(payload);
      dispatch(showSuccessSnackbar("User mapping updated successfully!"));
      navigate("/cpanel/map-user/list");
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to update mapping"));
    }
  };

  const cancelBtn = () => {
    navigate("/cpanel/map-user/list");
  };

  return (
    <FormProvider {...methods}>
      <PageWrapper>
        <StyledCard mb={6}>
          <HeaderContainer sx={{ pb: 4 }}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Edit User Mapping
            </Typography>
            <Breadcrumbs aria-label="Breadcrumb" sx={{ color: "rgba(255,255,255,0.7)" }}>
              <Typography color="inherit">C-Panel</Typography>
              <Typography color="inherit">User Mapping</Typography>
              <Typography color="white" fontWeight="600">
                Edit
              </Typography>
            </Breadcrumbs>
          </HeaderContainer>

          <CardContent sx={{ p: 0 }}>
            {isLoading || isSubmitting ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                my={12}
              >
                <CircularProgress thickness={5} size={60} />
                <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
                  {isLoading ? "Loading mapping details..." : "Updating mapping details..."}
                </Typography>
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormSection sx={{ pt: 6, pb: 4 }}>
                  <Box sx={{ maxWidth: 600 }}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ mb: 5, fontWeight: "bold", letterSpacing: "1px" }}
                    >
                      MAPPED USER
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: "600", color: "text.primary" }}>
                        {mapUser?.adminName || mapUser?.adminUsername || "Loading..."}
                      </Typography>
                    </Box>
                  </Box>
                </FormSection>

                <TabContainer>
                  <StyledTabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                  >
                    <StyledTab label="Franchise & Users" {...a11yProps(0)} />
                  </StyledTabs>
                </TabContainer>

                <Box sx={{ p: 2 }}>
                  <TabPanel value={tabValue} index={0}>
                    <DotSettingsFields />
                  </TabPanel>
                </Box>

                <Box sx={{ p: 4, borderTop: 1, borderColor: "divider", bgcolor: "#fafafa" }}>
                  <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
                </Box>
              </form>
            )}
          </CardContent>
        </StyledCard>
      </PageWrapper>
    </FormProvider>
  );
}

export default EditMapUser;
